import { useEffect, useState } from "react";
import { toast } from "sonner";
import { can, roleText, useOrg } from "./org";
import { PlanningCenter } from "./Pco";
import { RoleNames } from "./RoleNames";
import { VoiceSettings } from "./VoiceSettings";
import { Field, RhythmPicker } from "./people/bits";

const ZONES = Intl.supportedValuesOf ? Intl.supportedValuesOf("timeZone") : ["America/Chicago", "America/New_York", "America/Denver", "America/Los_Angeles", "UTC"];

export default function Settings() {
  const { api, membership, reload } = useOrg();
  const [f, setF] = useState(null);
  const [saved, setSaved] = useState(null);
  useEffect(() => {
    api("/org").then(({ organization: o }) => {
      const v = { name: o.name, timezone: o.timezone || "UTC", rhythm_days: o.rhythm_days, comfortable_load: o.comfortable_load ?? 12 };
      setF(v); setSaved(v);
    }).catch((e) => toast.error(e.message));
  }, [api]);
  if (!f) return <p className="t-meta">Loading…</p>;
  const changed = JSON.stringify(f) !== JSON.stringify(saved);
  const valid = f.name.trim().length >= 2 && f.rhythm_days && f.comfortable_load >= 1;
  const save = () => api("/org/settings", { method: "PATCH", body: { ...f, name: f.name.trim(), comfortable_load: Number(f.comfortable_load) } })
    .then((r) => { setSaved(r); setF(r); toast.success("Church settings saved"); reload(); }).catch((e) => toast.error(e.message));
  return (
    <div className="max-w-2xl" data-testid="settings-screen">
      <p className="t-label">{membership.org_name}</p>
      <h1 className="t-greeting mt-3">Church settings</h1>
      <div className="mt-10 space-y-8">
        <Field label="Church name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="field h-12 px-3" data-testid="settings-name" /></Field>
        <Field label="Time zone">
          <select value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} className="field h-12 px-3" data-testid="settings-timezone">
            {(ZONES.includes(f.timezone) ? ZONES : [f.timezone, ...ZONES]).map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
          </select>
          <p className="t-meta mt-2">Today, follow-ups and due dates follow this time zone for everyone, wherever they are.</p>
        </Field>
        <section>
          <h2 className="t-heading">Church-wide Care Rhythm</h2>
          <p className="t-lead mt-2">How often someone should hear from their Assigned Care Leader when nothing more specific applies.</p>
          <div className="mt-5"><RhythmPicker value={f.rhythm_days} onChange={(v) => setF({ ...f, rhythm_days: v })} testid="settings-rhythm" /></div>
          <p className="t-meta mt-4">Order used for each person: a scheduled check-in, then their active care rhythm, then their Care Group rhythm (shortest wins), then this default.</p>
        </section>
        <Field label={roleText(membership, "Comfortable Care Leader load")}>
          <div className="flex items-center gap-3"><input type="number" min={1} max={200} value={f.comfortable_load} onChange={(e) => setF({ ...f, comfortable_load: e.target.value ? Number(e.target.value) : "" })} className="field h-12 px-3 !w-28" data-testid="settings-load" /><span className="t-body text-ink-soft">people per caregiver</span></div>
          <p className="t-meta mt-2">Used to notice when someone may be carrying more than usual.</p>
        </Field>
      </div>
      <button disabled={!changed || !valid} onClick={save} className="btn btn-primary mt-10" data-testid="settings-save">Save settings</button>
      <RoleNames />
      <VoiceSettings />
      {can(membership).manage && <PlanningCenter />}
    </div>
  );
}
